import {
  Resource,
  PaginationData,
  ResourceInventoryEntry,
} from 'constants/interface';
import { InventoryEntryTypeEnum, InventoryTypeEnum } from 'constants/inventory';
import { ResourceCategoryEnum } from 'constants/resource';
import {
  buildPaginationParams,
  getPaginationHeaders,
  PaginatedResponse,
  requestClient,
} from 'infra/request';

export interface CreateResourceData extends Omit<
  Resource,
  'id' |
  'createdAt' |
  'updatedAt' |
  'createdBy' |
  'updatedBy'
> {}

export interface UpdateResourceData {
  id: string
  data: CreateResourceData
}

export interface GetResourceList extends PaginationData {
  category?: ResourceCategoryEnum
}

// RESOURCE ENTRIES

export interface GetResourceEntriesList extends PaginationData {
  type?: InventoryEntryTypeEnum
  inventoryType?: InventoryTypeEnum
  category?: ResourceCategoryEnum
  organization?: string
}

export interface ResourceApi {
  createResource(resourceData: CreateResourceData): Promise<Resource>
  updateResource(resourceData: UpdateResourceData): Promise<Resource>
  getResource(resourceId: string): Promise<Resource>
  getResourceList(filter: GetResourceList): Promise<PaginatedResponse<Resource>>

  getResourceEntriesList(filter: PaginationData): Promise<PaginatedResponse<ResourceInventoryEntry>>
}

const resourceApiBuilder = (): ResourceApi => {
  const createResource = async (
    resourceData: CreateResourceData,
  ): Promise<Resource> => {
    const params: Record<string, unknown> = {
      ...resourceData,
    };
    const result = await requestClient.post('/resources', params);

    const createdResource: Resource = result.data as Resource;
    return createdResource;
  };

  const updateResource = async (
    resourceData: UpdateResourceData,
  ): Promise<Resource> => {
    const { id: resourceId, data } = resourceData;
    const params: Record<string, unknown> = { ...data };
    const result = await requestClient.put(`/resources/${resourceId}`, params);
    const updatedResource: Resource = result.data as Resource;
    return updatedResource;
  };

  const getResource = async (
    resourceId: string,
  ): Promise<Resource> => {
    const result = await requestClient.get(`/resources/${resourceId}`);
    const resource: Resource = result.data as Resource;
    return resource;
  };

  const getResourceList = async (
    filterData: GetResourceList,
  ): Promise<PaginatedResponse<Resource>> => {
    const params = buildPaginationParams(filterData);
    const result = await requestClient.get('/resources', params);
    const pagination = getPaginationHeaders(result);
    const resourceList: Resource[] = result.data as Resource[];
    return {
      pagination,
      data: resourceList,
    };
  };

  // RESOURCE ENTRIES

  const getResourceEntriesList = async (
    filterData: GetResourceEntriesList,
  ): Promise<PaginatedResponse<ResourceInventoryEntry>> => {
    const params = buildPaginationParams(filterData);
    const result = await requestClient.get('/resources/entries', params);
    const pagination = getPaginationHeaders(result);
    const resourceEntriesList: ResourceInventoryEntry[] = result.data as ResourceInventoryEntry[];
    return {
      pagination,
      data: resourceEntriesList,
    };
  };

  return {
    createResource,
    updateResource,
    getResource,
    getResourceList,

    getResourceEntriesList,
  };
};

export const resourceApi = resourceApiBuilder();
